import { memo, useCallback, useMemo, useState } from 'react'

import StatsPopup from 'components/UI/StatsPopup/StatsPopup'
import { type IPost } from 'types/interfaces/ITypes'

interface Props {
  postData: IPost
  children: (controls: {
    openStats: () => void
    toggleStats: () => void
    isOpen: boolean
  }) => React.ReactNode
}

export const PostStatsControl = memo(({ postData, children }: Props) => {
  const [statsOpen, setStatsOpen] = useState(false)

  const openStats = useCallback(() => {
    setStatsOpen(true)
  }, [])

  const closeStats = useCallback(() => {
    setStatsOpen(false)
  }, [])

  const toggleStats = useCallback(() => {
    setStatsOpen((prev) => !prev)
  }, [])

  const statsData = useMemo(
    () => ({
      overallRevenue: postData.overall_revenue,
      purchasesCount: postData.purchases_count,
      purchasesSum: postData.purchases_sum,
      tipsCount: postData.tips_count,
      tipsSum: postData.tips_sum,
      conversionRate: postData.conversion_rate ? postData.conversion_rate : 0,
      uniqueImpressions: postData.unique_impressions,
      views: postData.views,
      uniqueViews: postData.unique_views,
      price: postData.price,
    }),
    [
      postData.overall_revenue,
      postData.purchases_count,
      postData.purchases_sum,
      postData.tips_count,
      postData.tips_sum,
      postData.conversion_rate,
      postData.unique_impressions,
      postData.views,
      postData.unique_views,
      postData.price,
    ]
  )

  const videoStats = useMemo(() => {
    const videos: any[] = postData.videos ?? []

    /*
     * Preview information now lives directly on the normal video.
     * A video gets a preview stats slide when a preview video exists.
     */
    const previewVideos = videos.filter(
      (video: any) => Boolean(video.preview_video_url)
    )
    const previewStats = previewVideos.map((video: any, index: number) => ({
      id: `preview-${video.id}`,
      videoId: video.id,
      index: index + 1,
      total: previewVideos.length,
      isPreview: true,
      title:
        previewVideos.length > 1
          ? `Preview Video ${index + 1} of ${previewVideos.length}`
          : 'Preview Video',

      // Dummy preview-video analytics for now.
      completionRate: 0,
      playsRaw: 0,
      playsUnique: 0,

      duration: Number(video.duration ?? 0),
      thumb: video.preview_thumb ?? null,
      locked: Boolean(video.locked),
    }))

    const regularStats = videos.map((video: any, index: number) => ({
      id: `video-${video.id}`,
      videoId: video.id,
      index: index + 1,
      total: videos.length,
      isPreview: false,
      title: `Video ${index + 1} of ${videos.length}`,
      completionRate: Number.isFinite(Number(video.completion_rate))
        ? Number(video.completion_rate)
        : 0,
      playsRaw: Number(video.plays_raw ?? 0),
      playsUnique: Number(video.plays_unique ?? 0),
      duration: Number(video.duration ?? 0),
      thumb: video.active_thumb ?? null,
      locked: Boolean(video.locked),
    }))

    return [...previewStats, ...regularStats]
  }, [postData.videos])

  return (
    <>
      {children({
        openStats,
        toggleStats,
        isOpen: statsOpen,
      })}

      {statsOpen && (
        <StatsPopup
          statsData={statsData}
          videoStats={videoStats}
          closeStats={closeStats}
        />
      )}
    </>
  )
})

PostStatsControl.displayName = 'PostStatsControl'